import type { GuideStructure } from '../api';
import type { ModuleReportData, GuideStructureData, DeepSearchCompletionData } from '../providers/DeepSearchCompletion';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Body } from '@workramp/ui';
import { v4 as uuidv4 } from 'uuid';
import { useActionCableSubscription } from '~/hooks';
import { useGenerateGuideStructureMutation } from '../api';
import { DeepSearchCompletionType } from '../providers/DeepSearchCompletion';
import { DeepResearchStepperMessage } from '../components/DeepResearchStepperMessage';
import { GuideStructureContent } from '../components/ChatThread/messages/GuideStructureContent';
import { ModuleReportMessage } from '../components/ChatThread/messages/ModuleReportMessage';

interface UseGenerateGuideStructureProps {
  pushSystemMessage: (message: string | React.ReactNode, size?: 'sm' | 'md') => void;
  pushLoadingState: () => void;
  stopLoadingState: () => void;
  onComplete?: (structure: GuideStructure, reports: ModuleReportData[]) => void;
}

export const useGenerateGuideStructure = ({
  pushSystemMessage,
  pushLoadingState,
  stopLoadingState,
  onComplete,
}: UseGenerateGuideStructureProps) => {
  const generateGuideStructureMutation = useGenerateGuideStructureMutation();
  const [structure, setStructure] = useState<GuideStructure>();
  const structureRef = useRef<GuideStructure | null>(null);
  structureRef.current = structure;

  const [moduleCount, setModuleCount] = useState<number>(0);
  const moduleCountRef = useRef<number>(moduleCount);

  const [reports, setReports] = useState<ModuleReportData[]>([]);
  const reportsRef = useRef<ModuleReportData[]>(reports);
  reportsRef.current = reports;

  const requestIdRef = useRef<string>();

  useEffect(() => {
    reportsRef.current = reports;
  }, [reports]);

  useEffect(() => {
    moduleCountRef.current = moduleCount;
  }, [moduleCount]);

  const [moduleIndex, setModuleIndex] = useState<number>(0);
  const moduleIndexRef = useRef<number>(moduleIndex);

  useEffect(() => {
    moduleIndexRef.current = moduleIndex;
  }, [moduleIndex]);

  const handleGenerateGuideStructure = useCallback(
    (topic: string, questions: string[], answers: Record<number, string>) => {
      pushSystemMessage(<Body level="sm">We are working on it...</Body>, 'sm');
      pushLoadingState();

      const newRequestId = uuidv4();
      requestIdRef.current = newRequestId;

      const contentData = {
        topic,
        questions,
        answers: Object.values(answers),
        requestId: newRequestId,
      };

      generateGuideStructureMutation.mutate(contentData, {
        onError: (error) => {
          pushSystemMessage(<Body level="sm">Error initiating content generation: {error.message}</Body>);
          stopLoadingState();
        },
      });

      pushSystemMessage(<DeepResearchStepperMessage requestId={newRequestId} onComplete={() => {}} />);
    },
    [generateGuideStructureMutation, pushSystemMessage, pushLoadingState, stopLoadingState],
  );

  const handleCompleted = useCallback(
    (data: ModuleReportData | GuideStructureData) => {
      if (data.type === DeepSearchCompletionType.GUIDE_STRUCTURE) {
        pushSystemMessage(<GuideStructureContent structure={data as GuideStructureData} />);
        pushLoadingState();
        setStructure(data as GuideStructureData);
        setModuleCount((data as GuideStructureData).modules.length);
      }
      if (data.type === DeepSearchCompletionType.MODULE_REPORT) {
        const module = structureRef.current?.modules[moduleIndexRef.current];
        if (module) {
          pushSystemMessage(<ModuleReportMessage module={module} report={JSON.parse(data.content)} />);
        }
        setModuleIndex((prev) => prev + 1);
        const reportData: ModuleReportData = JSON.parse(data.content);
        setReports((prev) => {
          if (prev) {
            return [...prev, reportData];
          }
          return [reportData];
        });
        if (moduleIndexRef.current === moduleCountRef.current - 1) {
          onComplete?.(structureRef.current, [...reportsRef.current, reportData]);
        } else {
          pushLoadingState();
        }
      }
    },
    [onComplete, pushLoadingState, pushSystemMessage],
  );

  useActionCableSubscription(
    {
      channel: 'DeepSearch::CompletionChannel',
    },
    {
      received: (message: DeepSearchCompletionData) => {
        if (message.status === 'completed' && message.request_id === requestIdRef.current) {
          const { data } = message;
          handleCompleted(data);
        }
      },
    },
    {
      isEnabled: true,
    },
  );

  useEffect(() => {
    structureRef.current = structure;
  }, [structure]);

  return {
    handleGenerateGuideStructure,
  };
};
